import React from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import ActivityActionsBar from './ActivityActionsBar/ActivityActionsBar';
import Collapse from 'components/Collapse/Collapse';
import ActivityBox from './ActivityBox';
import ActivityBodyCollapse from './ActivityBodyCollapse';
import ActivityHeader from './ActivityHeader';
import ActivityHeaderBadge from './ActivityHeader/ActivityHeaderBadge';
import ActivityMentions from './ActivityMentions';
import FarmTypeLabel from 'components/FarmTypeLabel';
import FormattedWeight from 'components/FormattedWeight';
import PigGroupMarketingBadge from 'components/PigGroupMarketingBadge';
import FarmFeedRowComments from 'components/FarmFeed/FarmFeedRowComments';
import FarmFeedRowAddComment from 'components/FarmFeed/FarmFeedRowAddComment';
import LastCommentInfo from './LastCommentInfo/LastCommentInfo';
import Divider from 'components/Divider';
import Link from 'react-router/lib/Link';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
// regional date handling
import { connect } from 'react-redux';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// hoc
import withActivityController from './hoc/withActivityController';
// styles
import styles from './GeneralWaterUsageWarning.module.scss';

const cn = classnames.bind(styles);

const affectRerenderKeys = ['id', 'updated_at', 'is_viewed', 'flagged', 'comments', 'additional_data'];

const GeneralWaterUsageWarning = ({
  className,
  activity,
  currentUser,
  isChildActivity,
  markAsSeen,
  openDropdown,
  setActivityFlag,
  isMinimized,
  isTruncated,
  toggleMinimized,
  onViewFullActivity,
  defaultDropdownOptions,
  countryCode,
}) => {
  const { id, events, created_at, flagged, is_viewed, mentions, additional_data } = activity;
  const isAdmin = currentUser.roles_map.admin;
  const threshold = additional_data?.threshold;

  const renderPercentageLabel = (event_data) => {
    const { water_usage_diff_percentage } = event_data;
    return water_usage_diff_percentage > 0 ? `+${water_usage_diff_percentage}%` : `${water_usage_diff_percentage}%`;
  };

  const renderIntegrationBadge = (event_data) => {
    const { integration } = event_data;

    return integration ? (<span className={cn('integration-badge')}>{integration}</span>) : null;
  };

  const renderFarmName = (farm, pig_group, event_data) => (
    <div>
      {isAdmin
        ? (
          <Link className="color-primary" to={`/barnsheets/groups/${pig_group.id}/water-usage`}>{farm.name}</Link>
        )
        : farm.name}
      <FarmTypeLabel className="ml-5" farmType={farm.farm_type} />
      <PigGroupMarketingBadge className="ml-5" size="small" pigGroup={pig_group} isMinimized isHintAttachedToBody />
      {renderIntegrationBadge(event_data)}
    </div>
  );

  return (
    <ActivityBox
      className={className}
      isFlagged={flagged}
      isTruncated={isTruncated}
      hasMarker={!isChildActivity}
      isUnseen={!is_viewed}
      markAsSeen={markAsSeen}
      onViewFullActivity={onViewFullActivity}
    >
      {!isMinimized &&
        <LastCommentInfo activity={activity} currentUser={currentUser} />
      }
      <ActivityHeader
        activityId={id}
        onOpenDropdown={(e) => openDropdown(e, defaultDropdownOptions)}
        onDoubleClick={toggleMinimized}
      >
        <ActivityHeaderBadge isCentered>
          <FormattedMessage id="general.waterUsageWarning" tagName="b" />
        </ActivityHeaderBadge>
      </ActivityHeader>
      <ActivityBodyCollapse isClosed={isMinimized}>
        <div className={cn('warning-text')}>
          <FormattedMessage
            tagName="div"
            id="component.generalWaterUsageWarning.text"
            values={{
              b: (weight) => <b>{weight}</b>,
              orange: (msg) => <span className={cn('brand-orange')}>{msg}</span>,
              date: formatDateForUser(moment(activity.created_at).subtract(1, 'day'), countryCode),
            }}
          />
          <div className={cn('timestamp')}>
            {moment(created_at).format('MMM DD, YYYY, hh:mm a')}
          </div>
          {threshold != null && (
            <div className={cn('threshold-reminder')}>
              <i className={`fa fa-filter color-primary ${cn('threshold-icon')}`} />
              <FormattedMessage
                id="component.generalWaterUsageWarning.threshold"
                values={{
                  b: (msg) => <b>{msg}</b>,
                  gray: (msg) => <span className={cn('muted')}>{msg}</span>,
                  value: threshold,
                }}
              />
            </div>
          )}
          <ActivityMentions isAdmin={isAdmin} users={mentions} />
        </div>
        {!!events.length && (
          <div className="ph-30 mb-25">
            <Collapse
              className={cn('collapse')}
              title={<FormattedMessage id="general.button.showDetails" />}
            >
              <Divider />
              <div className={cn('row', 'header-row')}>
                <div className={cn('farm-name')}>
                  <FormattedMessage id="general.farm" />
                </div>
                <div className={cn('weight')}>
                  <FormattedMessage id="component.generalWaterUsageWarning.estAvgWt" />
                </div>
                <div className={cn('value')}>
                  <FormattedMessage id="component.generalWaterUsageWarning.change24h" />
                </div>
              </div>
              {events.map(({ farm, pig_group, additional_data: event_data }, idx) => (
                <div className={cn('row')} key={idx}>
                  <div className={cn('farm-name')}>
                    {renderFarmName(farm, pig_group, event_data)}
                  </div>
                  <div className={cn('weight')}>
                    <FormattedWeight value={pig_group.estimated_weight} />
                  </div>
                  <div className={cn('value')}>
                    {renderPercentageLabel(event_data)}
                  </div>
                </div>
              ))}
            </Collapse>
          </div>
        )}
        {!isChildActivity && (
          <>
            <ActivityActionsBar
              activity={activity}
              currentUser={currentUser}
              openDropdown={openDropdown}
              setActivityFlag={setActivityFlag}
            />
            <FarmFeedRowComments activity={activity} />
            <FarmFeedRowAddComment activity={activity} />
          </>
        )}
      </ActivityBodyCollapse>
    </ActivityBox>
  );
};

GeneralWaterUsageWarning.contextTypes = {
  router: T.object.isRequired
};

GeneralWaterUsageWarning.propTypes = {
  className: T.string,
  activity: T.object.isRequired,
  currentUser: T.object.isRequired,
  markAsSeen: T.func,
  isChildActivity: T.bool,
  openDropdown: T.func.isRequired,
  setActivityFlag: T.func.isRequired,
  // from hoc
  isTruncated: T.bool.isRequired,
  isMinimized: T.bool.isRequired,
  toggleMinimized: T.func.isRequired,
  onViewFullActivity: T.func.isRequired,
  defaultDropdownOptions: T.array.isRequired,
  countryCode: T.string,
};

GeneralWaterUsageWarning.defaultProps = {
  isChildActivity: false,
};

const ConnectedComponent = connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(GeneralWaterUsageWarning);

export default withActivityController(ConnectedComponent, { affectRerenderKeys });
